import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API, { parseError } from "../utils/api";
import { Status, StatusColor, Color, Role } from "../constants";

function StatusBadge({ status }) {
  const sc = StatusColor[status] || {
    bg: "#F3F4F6",
    text: "#666",
    dot: "#999",
  };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
      style={{ backgroundColor: sc.bg, color: sc.text }}
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: sc.dot }}
      />
      {status}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="font-semibold text-gray-700 mb-4 text-sm uppercase tracking-wide">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="mb-3">
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className={`text-sm text-gray-800 ${mono ? "font-mono" : ""}`}>
        {value || <span className="text-gray-300">—</span>}
      </p>
    </div>
  );
}

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // const { user } = useAuth();
  const { user, isDelegated } = useAuth();

  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showCreatedBanner, setShowCreatedBanner] = useState(
    searchParams.get("created") === "1",
  );

  // Trainees for assign dropdown
  const [trainees, setTrainees] = useState([]);
  const [selectedST, setSelectedST] = useState("");
  const [managerNotes, setManagerNotes] = useState("");
  const [categories, setCategories] = useState([]);
  const [coolers, setCoolers] = useState([]);

  // Complete / not completed
  const [executionNotes, setExecutionNotes] = useState("");
  const [notCompletedReason, setNotCompletedReason] = useState("");
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [modalPhotos, setModalPhotos] = useState([]);
  const [showNotCompleteModal, setShowNotCompleteModal] = useState(false);
  const [sameStoreOrders, setSameStoreOrders] = useState([]);

  // Cancel
  const [cancelNotes, setCancelNotes] = useState("");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Photos
  const [uploading, setUploading] = useState(false);

  // Admin edit mode
  const [editMode, setEditMode] = useState(false);
  const [editDateMode, setEditDateMode] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [editDates, setEditDates] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stagedPhotos, setStagedPhotos] = useState([]);
  const [stagedDeleteIds, setStagedDeleteIds] = useState([]);
  const role = user?.role;
  const isSuperAdmin = user?.is_super_admin;
  const isManager = [Role.SUPER_ADMIN, Role.GSM, Role.DSM].includes(role);
  const isCreator = user?.email === order?.creator_email;
  const isST = role === Role.ST;
  const canManage = isManager || isDelegated(); // simplified — full scope check on backend
  const canSelfAssign = isST && order?.status === Status.PENDING;
  // A delegated ASM acts as a manager, so they may also take an order for
  // themselves (and hand it back) — same as GSM/DSM/SA.
  const canActAsManager =
    [Role.GSM, Role.DSM, Role.SA].includes(role) ||
    (role === Role.ASM && isDelegated());
  const canSelfAssignManager =
    canActAsManager && order?.status === Status.PENDING;
  const canUnassignSelf =
    canActAsManager &&
    order?.status === Status.ASSIGNED &&
    order?.assigned_to_id === user?.id;
  const canAssign = canManage && order?.status === Status.PENDING;
  const isAssignedToMe =
    order?.assigned_to_id === user?.id && order?.status === Status.ASSIGNED;
  const canComplete = isAssignedToMe;
  const canNotCompleted = isAssignedToMe;
  const canCancel =
    canManage &&
    !isST &&
    ![Status.COMPLETED, Status.CANCELLED].includes(order?.status);
  const canReopen =
    canManage &&
    !isST &&
    [Status.ASSIGNED, Status.CANCELLED].includes(order?.status) &&
    !(
      order?.assigned_to_id === user?.id &&
      [Role.GSM, Role.DSM, Role.SA].includes(role)
    );
  const canSoftDelete = isSuperAdmin && !order?.is_deleted;
  const canRestore = isSuperAdmin && order?.is_deleted;

  const fetchOrder = () => {
    API.get(`/work-orders/${id}`)
      .then((res) => {
        setOrder(res.data);
        setEditFields({
          task_category: res.data.task_category,
          cooler_type: res.data.cooler_type || "",
          store_contact: res.data.store_contact || "",
          asm_comments: res.data.asm_comments || "",
          manager_notes: res.data.manager_notes || "",
          status: res.data.status,
        });
        setEditDates({
          created_date: res.data.created_date?.slice(0, 16) || "",
          assigned_date: res.data.assigned_date?.slice(0, 16) || "",
          completed_date: res.data.completed_date?.slice(0, 16) || "",
          desired_completion_date: res.data.desired_completion_date || "",
        });
      })
      .catch(() => setError("Work order not found."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrder();
    API.get("/lookup/trainees")
      .then((r) => setTrainees(r.data))
      .catch(() => {});
    API.get("/lookup/categories")
      .then((r) => setCategories(r.data))
      .catch(() => {});
    API.get("/lookup/coolers")
      .then((r) => setCoolers(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!order?.customer_number) return;
    API.get("/work-orders", {
      params: { limit: 200 },
    })
      .then((r) => {
        const others = r.data.filter(
          (o) =>
            o.customer_number === order.customer_number &&
            o.id !== id &&
            ["Pending", "Assigned"].includes(o.status),
        );
        setSameStoreOrders(others);
      })
      .catch(() => {});
  }, [order?.customer_number, id]);

  const doAction = async (fn) => {
    setActionLoading(true);
    setActionError("");
    try {
      await fn();
      fetchOrder();
    } catch (err) {
      setActionError(parseError(err, "Action failed. Please try again."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = () =>
    doAction(async () => {
      const stId = isST ? user.id : selectedST;
      if (!stId) throw new Error("Please select a Sales Trainee");
      await API.patch(`/work-orders/${id}/assign`, {
        assigned_to_id: stId,
        manager_notes: managerNotes || null,
      });
      setSelectedST("");
      setManagerNotes("");
    });

  const handleSelfAssign = () =>
    doAction(async () => {
      await API.patch(`/work-orders/${id}/assign`, {
        assigned_to_id: user.id,
        manager_notes: managerNotes || null,
      });
      setManagerNotes("");
    });

  const handleUnassignSelf = () =>
    doAction(async () => {
      await API.patch(`/work-orders/${id}/unassign`);
    });

  const handleComplete = () =>
    doAction(async () => {
      await API.patch(`/work-orders/${id}/complete`, {
        st_execution_notes: executionNotes || null,
      });
      setExecutionNotes("");
    });

  const handleNotCompleted = () =>
    doAction(async () => {
      await API.patch(`/work-orders/${id}/not-completed`, {
        not_completed_reason: notCompletedReason || null,
      });
      setNotCompletedReason("");
    });

  const handleCancel = () =>
    doAction(async () => {
      await API.patch(`/work-orders/${id}/cancel`, {
        manager_notes: cancelNotes || null,
      });
      setShowCancelConfirm(false);
      setCancelNotes("");
    });

  const handleReopen = () =>
    doAction(async () => {
      await API.patch(`/work-orders/${id}/reopen`, { manager_notes: null });
    });

  const handleSoftDelete = () =>
    doAction(async () => {
      await API.patch(`/work-orders/${id}/soft-delete`);
      setShowDeleteConfirm(false);
    });

  const handleRestore = () =>
    doAction(async () => {
      await API.patch(`/work-orders/${id}/restore`);
    });

  const handleDeletePhoto = async (photoId) => {
    if (!editMode) {
      // Super admin immediate delete outside edit mode
      if (!window.confirm("Delete this photo?")) return;
      try {
        await API.delete(`/work-orders/${id}/photos/${photoId}`);
        fetchOrder();
      } catch (err) {
        setActionError(parseError(err, "Failed to delete photo."));
      }
    } else {
      // In edit mode — stage the delete
      setStagedDeleteIds((prev) => [...prev, photoId]);
    }
  };

  const handleCancelEdit = () => {
    stagedPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
    setStagedPhotos([]);
    setStagedDeleteIds([]);
    setEditMode(false);
  };

  const handleSaveChanges = () =>
    doAction(async () => {
      // 1. Save text fields first
      await API.patch(`/work-orders/${id}/edit`, editFields);
      // 2. Delete staged photos
      for (const photoId of stagedDeleteIds) {
        await API.delete(`/work-orders/${id}/photos/${photoId}`);
      }
      // 3. Upload staged new photos
      for (const p of stagedPhotos) {
        const fd = new FormData();
        fd.append("file", p.file);
        await API.post(`/work-orders/${id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      // 4. Clean up and exit
      stagedPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
      setStagedPhotos([]);
      setStagedDeleteIds([]);
      setEditMode(false);
    });

  const handleDateEdit = () =>
    doAction(async () => {
      await API.patch(`/work-orders/${id}/edit-dates`, editDates);
      setEditDateMode(false);
    });

  const fmt = (dateStr, withTime = false) => {
    if (!dateStr) return "—";
    // Plain date (e.g. "2026-05-26") — treat as local midnight, no Z
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
    const normalized = isDateOnly
      ? dateStr + "T00:00:00"
      : dateStr.endsWith("Z") || dateStr.includes("+")
        ? dateStr
        : dateStr + "Z";
    const d = new Date(normalized);
    const opts = { month: "short", day: "numeric", year: "numeric" };
    if (withTime)
      return d.toLocaleString("en-CA", {
        ...opts,
        hour: "2-digit",
        minute: "2-digit",
      });
    return d.toLocaleDateString("en-CA", opts);
  };

  const photoUrl = (photo) => {
    if (!photo) return "";
    // Backend provides a ready URL (presigned for R2, disk URL for local)
    if (photo.url) return photo.url;
    // Fallback for local dev disk paths
    const path = photo.file_path || "";
    if (path.startsWith("uploads/")) {
      const base =
        process.env.REACT_APP_API_URL ||
        `http://${window.location.hostname}:8000`;
      return `${base.replace(/\/$/, "")}/${path}`;
    }
    return "";
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="text-center py-20">
        <p className="text-gray-500 mb-4">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="text-sm underline text-gray-400"
        >
          ← Back to Work Orders
        </button>
      </div>
    );

  const formatAuditLog = (log) => {
    const { field_changed, old_value, new_value } = log;
    switch (field_changed) {
      case "status":
        return `changed status from "${old_value}" to "${new_value}"`;
      case "assigned_to":
        if (!old_value || old_value === "None")
          return `assigned order to ${new_value}`;
        if (!new_value || new_value === "None") return `unassigned themselves`;
        return `reassigned order to ${new_value}`;
      case "order_created":
        return `created this order`;
      case "order_reopened":
        return `reopened this order`;
      case "edited_task_category":
        return `changed task category to "${new_value}"`;
      case "edited_cooler_type":
        return !new_value || new_value === "None"
          ? `removed cooler type`
          : `changed cooler to "${new_value}"`;
      case "edited_store_contact":
        return !new_value || new_value === "None"
          ? `removed store contact`
          : !old_value || old_value === "None"
            ? `added store contact "${new_value}"`
            : `changed store contact to "${new_value}"`;
      case "edited_asm_comments":
        return !new_value || new_value === "None"
          ? `cleared comments`
          : !old_value || old_value === "None"
            ? `added comments`
            : `updated comments`;
      case "edited_manager_notes":
        return !new_value || new_value === "None"
          ? `cleared manager notes`
          : `updated manager notes`;
      case "edited_status":
        return `changed status to "${new_value}"`;
      case "order_edited":
        // Legacy fallback for old records
        if (!old_value || old_value === "None")
          return new_value
            ? `set a field to "${new_value}"`
            : `updated order details`;
        if (!new_value || new_value === "None") return `cleared "${old_value}"`;
        return `changed "${old_value}" → "${new_value}"`;
      case "photo_added":
        return `added a photo`;
      case "photo_removed":
        return `deleted a photo`;
      case "manager_notes":
        return new_value ? `added manager notes` : `cleared manager notes`;
      case "st_execution_notes":
        return `added execution notes`;
      case "not_completed_reason":
        return `noted: "${new_value}"`;
      case "date_override":
        return `edited dates`;
      default:
        return `updated ${field_changed.replace(/_/g, " ")}`;
    }
  };

  return (
    <div className="max-w-4xl mx-auto pb-20 md:pb-0">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="text-gray-400 hover:text-gray-600 text-sm flex items-center gap-1 mb-4"
      >
        ← Back to Work Orders
      </button>

      {/*
        On a phone the link above scrolls away, and the browser's own back
        button is easy to miss. This stays put. navigate(-1) returns to the
        list with its filters intact, which a fresh /work-orders link would
        not do.
      */}
      <button
        onClick={() => navigate(-1)}
        className="md:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-40
                   flex items-center gap-1.5 px-5 py-3 rounded-full shadow-lg
                   text-sm font-semibold text-white whitespace-nowrap"
        style={{ backgroundColor: Color.NAVY }}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        All orders
      </button>

      {/* Created banner */}
      {showCreatedBanner && (
        <div
          className="mb-4 p-3 rounded-lg flex items-center justify-between"
          style={{ backgroundColor: "#E8F5E9", color: "#005700" }}
        >
          <span className="text-sm font-medium">
            ✓ Work order created successfully
          </span>
          <button
            onClick={() => setShowCreatedBanner(false)}
            className="text-xs opacity-60"
          >
            ✕
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-800">
              {order.work_order_number || order.id.slice(0, 8)}
            </h1>
            <StatusBadge status={order.status} />
            {order.is_overdue && (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: "#FDECEA", color: Color.RED }}
              >
                Overdue
              </span>
            )}
            {order.attempt_count > 1 && (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: "#FFF8E1", color: "#B38600" }}
              >
                Attempt #{order.attempt_count}
              </span>
            )}
          </div>
          <p className="text-gray-500 text-sm mt-1">
            Created {fmt(order.created_date, true)} by {order.creator_name}
          </p>
        </div>

        {/* Super Admin controls */}
        {(isSuperAdmin || isCreator) && (
          <div className="flex gap-2 flex-wrap flex-shrink-0">
            {editMode ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium border transition"
                  style={{ borderColor: "#9CA3AF", color: "#6B7280" }}
                >
                  Cancel Edit
                </button>
                <button
                  onClick={handleSaveChanges}
                  disabled={actionLoading}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium border transition disabled:opacity-50"
                  style={{
                    backgroundColor: Color.NAVY,
                    color: "#fff",
                    borderColor: Color.NAVY,
                  }}
                >
                  {actionLoading ? "Saving..." : "Save Changes"}
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setEditMode(true);
                  setEditDateMode(false);
                }}
                disabled={editDateMode}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition
                  ${editDateMode ? "opacity-40 cursor-not-allowed" : ""}`}
                style={{ borderColor: Color.NAVY, color: Color.NAVY }}
              >
                Edit Order
              </button>
            )}
            {/* Edit Dates button — commented out, Super Admin only feature
            <button
              onClick={() => {
                setEditDateMode(!editDateMode);
                setEditMode(false);
              }}
              disabled={editMode}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition
                ${editMode ? "opacity-40 cursor-not-allowed" : ""}`}
              style={
                editDateMode
                  ? {
                      backgroundColor: "#B38600",
                      color: "#fff",
                      borderColor: "#B38600",
                    }
                  : { borderColor: "#B38600", color: "#B38600" }
              }
            >
              {editDateMode ? "Cancel Edit" : "Edit Dates"}
            </button>
            */}

            {canSoftDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={editMode || editDateMode}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition
                  ${editMode || editDateMode ? "opacity-40 cursor-not-allowed" : ""}`}
                style={{ borderColor: Color.RED, color: Color.RED }}
              >
                Delete Order
              </button>
            )}
            {canRestore && (
              <button
                onClick={handleRestore}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border transition"
                style={{ borderColor: Color.GREEN, color: Color.GREEN }}
              >
                Restore Order
              </button>
            )}
          </div>
        )}
      </div>

      {/* Same-store warning */}
      {sameStoreOrders.length > 0 && (
        <div
          className="mb-5 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: "#FFF8E1",
            color: "#B38600",
            border: "1px solid #FFBE00",
          }}
        >
          ⚠ This store has{" "}
          <strong>
            {sameStoreOrders.length} other open{" "}
            {sameStoreOrders.length === 1 ? "order" : "orders"}
          </strong>{" "}
          — make sure to check them.
          <div className="mt-1 space-y-0.5">
            {sameStoreOrders.map((o) => (
              <div key={o.id} className="text-xs text-gray-500">
                • {o.work_order_number || o.id.slice(0, 8)} — {o.task_category}{" "}
                ({o.status})
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Warning banners — separate row, never affect button positions */}
      {(editDateMode || order.is_deleted) && (
        <div className="mb-5 space-y-2">
          {editDateMode && (
            <div
              className="p-3 rounded-lg text-sm"
              style={{
                backgroundColor: "#FFF8E1",
                color: "#B38600",
                border: "1px solid #FFBE00",
              }}
            >
              ⚠ Date Override Mode — changes affect scheduling algorithms and
              audit logs
            </div>
          )}
          {order.is_deleted && (
            <div
              className="p-3 rounded-lg text-sm"
              style={{
                backgroundColor: "#FDECEA",
                color: Color.RED,
                border: `1px solid ${Color.RED}`,
              }}
            >
              ⚠ This order has been soft-deleted and is hidden from other users
            </div>
          )}
        </div>
      )}

      {actionError && (
        <div
          className="mb-4 p-3 rounded-lg text-sm border"
          style={{
            backgroundColor: "#FDECEA",
            borderColor: "#FFCDD2",
            color: "#A00028",
          }}
        >
          {actionError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* ── Order Details ── */}
        <Section title="Order Details">
          {editMode ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">
                  Task Category
                </label>
                <select
                  value={editFields.task_category}
                  onChange={(e) =>
                    setEditFields((f) => ({
                      ...f,
                      task_category: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">
                  Cooler Type
                </label>
                <select
                  value={editFields.cooler_type}
                  onChange={(e) =>
                    setEditFields((f) => ({
                      ...f,
                      cooler_type: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="">— None —</option>
                  {coolers.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">
                  Store Contact
                </label>
                <input
                  value={editFields.store_contact}
                  onChange={(e) =>
                    setEditFields((f) => ({
                      ...f,
                      store_contact: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              {isSuperAdmin && (
                <div>
                  <label className="text-xs text-gray-400 block mb-1">
                    Status
                  </label>
                  <select
                    value={editFields.status}
                    onChange={(e) =>
                      setEditFields((f) => ({ ...f, status: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {Object.values(Status).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {/* Save Changes moved to top bar
              <button
                onClick={handleAdminEdit}
                disabled={actionLoading}
                className="w-full py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: Color.NAVY }}
              >
                {actionLoading ? "Saving..." : "Save Changes"}
              </button>
              */}
            </div>
          ) : (
            <>
              <Field
                label="Customer"
                value={`${order.customer_name} (#${order.customer_number})`}
              />
              <Field
                label="Address"
                value={`${order.address || ""}, ${order.city || ""}`}
              />
              <Field label="Route" value={order.route} mono />
              <Field label="Task Category" value={order.task_category} />
              {order.cooler_type && (
                <Field label="Cooler Model" value={order.cooler_type} />
              )}
              {order.store_contact && (
                <Field label="Store Contact" value={order.store_contact} />
              )}
            </>
          )}
        </Section>
        {/* ── Dates ── */}
        <Section title="Dates">
          {editDateMode ? (
            <div className="space-y-3">
              {[
                {
                  key: "created_date",
                  label: "Created Date",
                  type: "datetime-local",
                },
                {
                  key: "desired_completion_date",
                  label: "Desired Completion",
                  type: "date",
                },
                {
                  key: "assigned_date",
                  label: "Assigned Date",
                  type: "datetime-local",
                },
                {
                  key: "completed_date",
                  label: "Completed Date",
                  type: "datetime-local",
                },
              ].map(({ key, label, type }) => (
                <div key={key}>
                  <label className="text-xs text-gray-400 block mb-1">
                    {label}
                  </label>
                  <input
                    type={type}
                    value={editDates[key]}
                    onChange={(e) =>
                      setEditDates((d) => ({ ...d, [key]: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              ))}
              <button
                onClick={handleDateEdit}
                disabled={actionLoading}
                className="w-full py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: "#B38600" }}
              >
                {actionLoading ? "Saving..." : "Save Dates"}
              </button>
            </div>
          ) : (
            <>
              <Field label="Created" value={fmt(order.created_date, true)} />
              <Field
                label="Desired Completion"
                value={fmt(order.desired_completion_date)}
              />
              <Field label="Assigned" value={fmt(order.assigned_date, true)} />
              <Field
                label="Completed"
                value={fmt(order.completed_date, true)}
              />
            </>
          )}
        </Section>
        {/* ── Notes ── */}
        <Section title="Notes & Comments">
          {editMode ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">
                  Creator Comments
                </label>
                <textarea
                  value={editFields.asm_comments}
                  onChange={(e) =>
                    setEditFields((f) => ({
                      ...f,
                      asm_comments: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                />
              </div>
              {isSuperAdmin && (
                <div>
                  <label className="text-xs text-gray-400 block mb-1">
                    Manager Notes
                  </label>
                  <textarea
                    value={editFields.manager_notes}
                    onChange={(e) =>
                      setEditFields((f) => ({
                        ...f,
                        manager_notes: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                  />
                </div>
              )}
            </div>
          ) : (
            <>
              <Field label="Creator Comments" value={order.asm_comments} />
              <Field label="Manager Notes" value={order.manager_notes} />
              <Field label="Execution Notes" value={order.st_execution_notes} />
              {order.not_completed_reason && (
                <div
                  className="p-3 rounded-lg mt-2"
                  style={{ backgroundColor: "#FDECEA" }}
                >
                  <p className="text-xs text-gray-400 mb-0.5">
                    Not Completed Reason
                  </p>
                  <p className="text-sm" style={{ color: Color.RED }}>
                    {order.not_completed_reason}
                  </p>
                </div>
              )}
            </>
          )}
        </Section>
        {/* ── Assignment ── */}
        <Section title="Assignment">
          <Field label="Assigned To" value={order.assigned_to} />
          <Field
            label="Creator"
            value={`${order.creator_name} (${order.creator_email})`}
          />
          {order.creator_phone && (
            <Field label="Creator Phone" value={order.creator_phone} />
          )}

          {/* Manager assign */}
          {canAssign && (
            <div className="mt-4 space-y-2 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 font-medium">
                Assign to Sales Trainee
              </p>
              <select
                value={selectedST}
                onChange={(e) => setSelectedST(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Select Sales Trainee...</option>
                {trainees.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <textarea
                value={managerNotes}
                onChange={(e) => setManagerNotes(e.target.value)}
                placeholder="Manager notes / instructions (optional)"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
              />
              <button
                onClick={handleAssign}
                disabled={actionLoading || !selectedST}
                className="w-full py-2 rounded-lg text-sm font-medium text-white transition
                           disabled:opacity-50"
                style={{ backgroundColor: Color.LIGHT_BLUE }}
              >
                {actionLoading ? "Assigning..." : "Assign Order"}
              </button>
            </div>
          )}

          {/* ST self-assign */}
          {canSelfAssign && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={handleAssign}
                disabled={actionLoading}
                className="w-full py-2 rounded-lg text-sm font-medium text-white transition
                           disabled:opacity-50"
                style={{ backgroundColor: Color.LIGHT_BLUE }}
              >
                {actionLoading ? "Assigning..." : "Assign to myself"}
              </button>
            </div>
          )}

          {/* Manager self-assign (GSM/DSM/SA) */}
          {canSelfAssignManager && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={handleSelfAssign}
                disabled={actionLoading}
                className="w-full py-2 rounded-lg text-sm font-medium text-white transition
                           disabled:opacity-50"
                style={{ backgroundColor: Color.LIGHT_BLUE }}
              >
                {actionLoading ? "Assigning..." : "Assign to myself"}
              </button>
            </div>
          )}

          {/* Unassign self (GSM/DSM/SA only) */}
          {canUnassignSelf && (
            <div className="mt-3">
              <button
                onClick={handleUnassignSelf}
                disabled={actionLoading}
                className="w-full py-2 rounded-lg text-sm font-medium border transition
                           disabled:opacity-50"
                style={{ borderColor: Color.NAVY, color: Color.NAVY }}
              >
                {actionLoading ? "..." : "Unassign myself"}
              </button>
            </div>
          )}

          {/* Reopen */}
          {canReopen && (
            <div className="mt-3">
              <button
                onClick={handleReopen}
                disabled={actionLoading}
                className="w-full py-2 rounded-lg text-sm font-medium border transition"
                style={{ borderColor: Color.NAVY, color: Color.NAVY }}
              >
                {actionLoading ? "..." : "Reopen to Pending"}
              </button>
            </div>
          )}

          {/* Cancel */}
          {canCancel && (
            <div className="mt-3">
              {!showCancelConfirm ? (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="w-full py-2 rounded-lg text-sm font-medium border transition"
                  style={{ borderColor: Color.GRAY, color: Color.GRAY }}
                >
                  Cancel Order
                </button>
              ) : (
                <div
                  className="space-y-2 p-3 rounded-lg border"
                  style={{ borderColor: Color.GRAY }}
                >
                  <p className="text-xs text-gray-500 font-medium">
                    Confirm cancellation
                  </p>
                  <textarea
                    value={cancelNotes}
                    onChange={(e) => setCancelNotes(e.target.value)}
                    placeholder="Reason for cancellation (optional)"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowCancelConfirm(false)}
                      className="flex-1 py-2 rounded-lg text-sm border border-gray-300 text-gray-600"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={actionLoading}
                      className="flex-1 py-2 rounded-lg text-sm font-medium text-white"
                      style={{ backgroundColor: Color.GRAY }}
                    >
                      {actionLoading ? "..." : "Confirm Cancel"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>
        {/* ── Photos ── */}
        <div className="md:col-span-2">
          <Section title={`Photos (${order.photos?.length || 0})`}>
            <div className="flex flex-wrap gap-3">
              {order.photos?.map((photo) => (
                <div
                  key={photo.id}
                  className="relative w-32 h-32 rounded-lg overflow-hidden border border-gray-200 group cursor-pointer"
                  onClick={() => setLightboxPhoto(photo)}
                >
                  <img
                    src={photoUrl(photo)}
                    alt=""
                    className="w-full h-full object-cover cursor-pointer"
                  />
                  <div
                    className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20
                                  transition-all"
                  />
                  {(isSuperAdmin || (isCreator && editMode)) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePhoto(photo.id);
                      }}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full text-white
                                 text-xs flex items-center justify-center opacity-0
                                 group-hover:opacity-100 transition-opacity"
                      style={{ backgroundColor: Color.RED }}
                    >
                      ✕
                    </button>
                  )}
                  {stagedDeleteIds.includes(photo.id) && (
                    <div className="absolute inset-0 bg-red-500 bg-opacity-50 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">
                        Deleting
                      </span>
                    </div>
                  )}
                  <div
                    className="absolute bottom-0 left-0 right-0 p-1 text-xs text-white
                                  bg-gradient-to-t from-black/60 to-transparent"
                  >
                    {photo.uploaded_by_name?.split(" ")[0]}
                  </div>
                </div>
              ))}
            </div>
            {isCreator &&
              editMode &&
              (order.photos?.length || 0) -
                stagedDeleteIds.length +
                stagedPhotos.length <
                3 && (
                <div className="mt-3">
                  <label
                    className="w-24 h-24 flex flex-col items-center justify-center
                      border-2 border-dashed border-gray-300 rounded-lg
                      cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition"
                  >
                    <svg
                      className="w-6 h-6 text-gray-400 mb-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    <span className="text-xs text-gray-400">Add photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        const remaining =
                          3 -
                          (order.photos?.length || 0) +
                          stagedDeleteIds.length -
                          stagedPhotos.length;
                        const toAdd = files.slice(0, remaining).map((file) => ({
                          file,
                          preview: URL.createObjectURL(file),
                        }));
                        setStagedPhotos((prev) => [...prev, ...toAdd]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              )}
            {stagedPhotos.map((p, i) => (
              <div
                key={i}
                className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-blue-400 group"
              >
                <img
                  src={p.preview}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-blue-500 bg-opacity-20" />
                <button
                  onClick={() => {
                    URL.revokeObjectURL(p.preview);
                    setStagedPhotos((prev) =>
                      prev.filter((_, idx) => idx !== i),
                    );
                  }}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full text-white
                 text-xs flex items-center justify-center"
                  style={{ backgroundColor: Color.RED }}
                >
                  ✕
                </button>
                <div
                  className="absolute bottom-0 left-0 right-0 p-1 text-xs text-white
                    bg-gradient-to-t from-black/60 to-transparent"
                >
                  New
                </div>
              </div>
            ))}
          </Section>
        </div>
        {/* ── ST Actions ── */}
        {(canComplete || canNotCompleted) && (
          <div className="md:col-span-2">
            <Section title="Complete or Report">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Complete */}
                {canComplete && (
                  <div className="space-y-2">
                    <button
                      onClick={() => setShowCompleteModal(true)}
                      disabled={actionLoading}
                      className="w-full py-2.5 rounded-lg text-sm font-medium text-white"
                      style={{ backgroundColor: Color.GREEN }}
                    >
                      ✓ Mark as Completed
                    </button>
                  </div>
                )}

                {/* Not completed */}
                {canNotCompleted && (
                  <div className="space-y-2">
                    <button
                      onClick={() => setShowNotCompleteModal(true)}
                      disabled={actionLoading}
                      className="w-full py-2.5 rounded-lg text-sm font-medium text-white"
                      style={{ backgroundColor: Color.RED }}
                    >
                      ✕ Mark as Not Completed
                    </button>
                  </div>
                )}
              </div>
            </Section>
          </div>
        )}
        {/* ── Previous Attempts ── */}
        {order.attempt_count > 1 && (
          <div className="md:col-span-2">
            <Section title={`Previous Attempts (${order.attempt_count - 1})`}>
              <div className="space-y-3">
                {order.audit_logs
                  ?.filter(
                    (l) =>
                      l.field_changed === "status" &&
                      l.new_value === "Not Completed",
                  )
                  .map((log, i) => {
                    const reason = order.audit_logs.find(
                      (r) =>
                        r.field_changed === "not_completed_reason" &&
                        r.attempt_number === log.attempt_number,
                    );
                    return (
                      <div
                        key={log.id}
                        className="p-3 rounded-lg border"
                        style={{
                          borderColor: "#FFCDD2",
                          backgroundColor: "#FFF8F8",
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">
                            Attempt #{log.attempt_number} —{" "}
                            {log.changed_by_name || log.changed_by}
                          </span>
                          <span className="text-xs text-gray-400">
                            {fmt(log.timestamp, true)}
                          </span>
                        </div>
                        {reason && (
                          <p className="text-sm text-gray-600">
                            <span className="text-gray-400">Reason: </span>
                            {reason.new_value}
                          </p>
                        )}
                      </div>
                    );
                  })}
              </div>
            </Section>
          </div>
        )}
        {/* ── Audit Log ── */}
        <div className="md:col-span-2">
          <Section title="Audit Log">
            {order.audit_logs?.length === 0 ? (
              <p className="text-sm text-gray-400">No changes recorded yet.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {[...order.audit_logs]
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((log) => (
                    <div
                      key={log.id}
                      className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0"
                    >
                      <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-gray-300" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">
                            {log.changed_by_name || log.changed_by}
                          </span>{" "}
                          <span className="text-gray-500">
                            {formatAuditLog(log)}
                          </span>
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {fmt(log.timestamp, true)}
                          {log.role_at_time && ` · ${log.role_at_time}`}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </Section>
        </div>
      </div>

      {/* Photo lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.9)" }}
          onClick={() => setLightboxPhoto(null)}
        >
          <div
            className="relative max-w-4xl max-h-full w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photoUrl(lightboxPhoto)}
              alt=""
              className="w-full h-auto max-h-screen object-contain rounded-lg"
            />
            <div
              className="absolute bottom-0 left-0 right-0 p-3 rounded-b-lg
                            flex items-center justify-between"
              style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
            >
              <span className="text-white text-sm">
                {lightboxPhoto.uploaded_by_name} ·{" "}
                {new Date(lightboxPhoto.uploaded_at + "Z").toLocaleString(
                  "en-CA",
                  {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )}
              </span>
              <button
                onClick={() => setLightboxPhoto(null)}
                className="text-white text-sm px-3 py-1 rounded-lg"
                style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
              >
                ✕ Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete confirmation modal */}
      {showCompleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="font-bold text-gray-800 text-lg mb-4">
              Mark as Completed
            </h3>

            {/* Execution notes */}
            <p className="text-xs text-gray-500 font-medium mb-1">
              Execution Notes (optional)
            </p>
            <textarea
              value={executionNotes}
              onChange={(e) => setExecutionNotes(e.target.value)}
              placeholder="Describe what was done..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none mb-4"
            />

            {/* Photo upload */}
            <p className="text-xs text-gray-500 font-medium mb-2">
              Photos (optional)
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {modalPhotos.map((f, i) => (
                <div
                  key={i}
                  className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
                >
                  <img
                    src={URL.createObjectURL(f)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() =>
                      setModalPhotos((p) => p.filter((_, j) => j !== i))
                    }
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full text-white text-xs
                               flex items-center justify-center"
                    style={{ backgroundColor: Color.RED }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              {modalPhotos.length < 6 && (
                <label
                  className="w-20 h-20 flex flex-col items-center justify-center
                                  border-2 border-dashed border-gray-300 rounded-lg cursor-pointer
                                  hover:border-blue-400 hover:bg-blue-50 transition"
                >
                  <svg
                    className="w-5 h-5 text-gray-400 mb-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <span className="text-xs text-gray-400">Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files[0];
                      if (f) setModalPhotos((p) => [...p, f]);
                      e.target.value = "";
                    }}
                  />
                </label>
              )}
            </div>

            {uploading && (
              <p className="text-xs text-gray-400 mb-3">Uploading photos...</p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setModalPhotos([]);
                }}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Upload photos first, then complete
                  setUploading(true);
                  try {
                    for (const file of modalPhotos) {
                      const fd = new FormData();
                      fd.append("file", file);
                      await API.post(`/work-orders/${id}/photos`, fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                      });
                    }
                  } catch (err) {
                    setActionError(parseError(err, "Photo upload failed."));
                    setUploading(false);
                    return;
                  }
                  setUploading(false);
                  setModalPhotos([]);
                  setShowCompleteModal(false);
                  handleComplete();
                }}
                disabled={actionLoading || uploading}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: Color.GREEN }}
              >
                {uploading
                  ? "Uploading..."
                  : actionLoading
                    ? "Saving..."
                    : "✓ Confirm Complete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Not Completed modal */}
      {showNotCompleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: "#FDECEA" }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: Color.RED }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h3 className="text-center font-bold text-gray-800 text-lg mb-1">
              Mark as Not Completed?
            </h3>
            <p className="text-center text-gray-500 text-sm mb-4">
              Please provide a reason.
            </p>
            <textarea
              value={notCompletedReason}
              onChange={(e) => setNotCompletedReason(e.target.value)}
              placeholder="Reason why task could not be completed..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowNotCompleteModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowNotCompleteModal(false);
                  handleNotCompleted();
                }}
                disabled={actionLoading}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: Color.RED }}
              >
                {actionLoading ? "Saving..." : "✕ Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: "#FDECEA" }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: Color.RED }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">
              Delete Work Order?
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              This order will be hidden from all other users. You can restore it
              later from the Work Orders list.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm
                           font-medium text-gray-600 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSoftDelete}
                disabled={actionLoading}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white
                           transition disabled:opacity-50"
                style={{ backgroundColor: Color.RED }}
              >
                {actionLoading ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
